import React, { useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { getBalanceSheet } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Direct port of digicampusbd's `general-accounts-report-balance-sheet`
 * ("Financial Balance Sheet") — cumulative Asset/Liability balance per
 * ledger, as of one chosen date (not a date range), using each category's
 * Debit/Credit nature to sign the balance, plus a Profit/Loss balancing
 * figure derived from all-time Income minus Expense. Confirmed via
 * `AccountTransactionDetailsRepository.findBalanceSheet`'s native query —
 * new `GET /api/accounts/vouchers/reports/balance-sheet` mirrors it verbatim.
 */
export default function BalanceSheetPage() {
  const [trnDate, setTrnDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setData(await getBalanceSheet(trnDate));
    } catch (err) {
      setError("Failed to load balance sheet.");
    } finally {
      setLoading(false);
    }
  };

  const assetGroups = (data?.categories || []).filter((c) => c.categoryType === "Asset");
  const liabilityGroups = (data?.categories || []).filter((c) => c.categoryType === "Liability");
  const totalAssets = assetGroups.reduce((s, g) => s + g.totalBalance, 0);
  const totalLiabilities = liabilityGroups.reduce((s, g) => s + g.totalBalance, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Financial Balance Sheet</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          As Of Date
          <input type="date" value={trnDate} onChange={(e) => setTrnDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        data && (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div className="table-scroll" style={{ flex: 1, minWidth: 320 }}>
              <h3>Assets</h3>
              {assetGroups.map((g) => (
                <table className="attendance-table" key={g.categoryName} style={{ marginBottom: 12 }}>
                  <thead>
                    <tr>
                      <th colSpan={2}>{g.categoryName}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.ledgers.map((l, i) => (
                      <tr key={i}>
                        <td>{l.ledgerName}</td>
                        <td>{l.balance.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ fontWeight: 800 }}>Subtotal</td>
                      <td>
                        <strong>{g.totalBalance.toFixed(2)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ))}
              <p style={{ fontWeight: 800 }}>Total Assets: {totalAssets.toFixed(2)}</p>
            </div>
            <div className="table-scroll" style={{ flex: 1, minWidth: 320 }}>
              <h3>Liabilities &amp; Equity</h3>
              {liabilityGroups.map((g) => (
                <table className="attendance-table" key={g.categoryName} style={{ marginBottom: 12 }}>
                  <thead>
                    <tr>
                      <th colSpan={2}>{g.categoryName}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.ledgers.map((l, i) => (
                      <tr key={i}>
                        <td>{l.ledgerName}</td>
                        <td>{l.balance.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ fontWeight: 800 }}>Subtotal</td>
                      <td>
                        <strong>{g.totalBalance.toFixed(2)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ))}
              <p>
                {data.profitLossLabel} (retained): {data.profitLossValue.toFixed(2)}
              </p>
              <p style={{ fontWeight: 800 }}>
                Total Liabilities &amp; Equity + {data.profitLossLabel}:{" "}
                {(totalLiabilities + (data.profitLossLabel === "Profit" ? data.profitLossValue : -data.profitLossValue)).toFixed(2)}
              </p>
            </div>
          </div>
        )
      )}
    </MainLayout>
  );
}
